"use client";

import { useCallback, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { readBrowserAttributionWithCurrentUrl } from "@/components/analytics/browser-attribution";
import {
  FREE_JOB_PACK_OFFER,
  FREE_JOB_PACK_CTA_TEXT,
  freeJobPackHref,
  type FreeJobPackPreset,
} from "@/lib/marketing/free-job-pack";

interface FreeJobPackDraft {
  trackingKey: string;
  title: string;
  ctaLabel: string;
  scopeDescription: string;
  domesticClient?: boolean;
  estimatedDuration?: string;
}

export function FreeJobPackStartCta({
  preset,
  draft,
  utmContent,
  ctaLocation,
  className,
  children,
}: {
  preset?: FreeJobPackPreset | null;
  draft?: FreeJobPackDraft | null;
  utmContent: string;
  ctaLocation: string;
  className: string;
  children?: ReactNode;
}) {
  const router = useRouter();
  const posthog = usePostHog();
  const [isStarting, setIsStarting] = useState(false);
  const fallbackHref = freeJobPackHref({ utmContent, presetKey: preset?.key });
  const draftDetails = preset ?? draft;
  const label = children ?? draftDetails?.ctaLabel ?? FREE_JOB_PACK_CTA_TEXT;

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      if (!draftDetails || isStarting) return;

      event.preventDefault();
      setIsStarting(true);

      const attribution = readBrowserAttributionWithCurrentUrl();
      const attributionPayload = attribution ?? {};

      try {
        const createRes = await fetch("/api/draft-projects", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            roleIntent: "RUNNING_THE_JOB",
            title: draftDetails.title,
            ...attributionPayload,
            utmOffer: attribution?.utmOffer ?? FREE_JOB_PACK_OFFER,
            utmContent: attribution?.utmContent ?? utmContent,
          }),
        });
        if (!createRes.ok) throw new Error("create_failed");

        const createdDraft = (await createRes.json()) as { sessionToken?: string };
        if (!createdDraft.sessionToken) throw new Error("missing_session");

        const patchRes = await fetch(`/api/draft-projects/${createdDraft.sessionToken}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            scopeDescription: draftDetails.scopeDescription,
            domesticClient: draftDetails.domesticClient ?? true,
            estimatedDuration: draftDetails.estimatedDuration ?? "1-4 weeks",
          }),
        });
        if (!patchRes.ok) throw new Error("patch_failed");

        posthog?.capture?.("free_job_pack_preset_started", {
          preset: preset?.key ?? draft?.trackingKey,
          cta_location: ctaLocation,
          cta_label: draftDetails.ctaLabel,
          destination: "/start-project/brief",
        });

        router.push("/start-project/brief");
      } catch {
        router.push(fallbackHref);
      } finally {
        setIsStarting(false);
      }
    },
    [ctaLocation, draft?.trackingKey, draftDetails, fallbackHref, isStarting, posthog, preset?.key, router, utmContent],
  );

  return (
    <a href={fallbackHref} onClick={handleClick} className={className}>
      {isStarting ? "Starting..." : label}
    </a>
  );
}
